import { defineStore } from "pinia";
import { createBaseStoreRefactor } from "./baseStoreRefactor";
import apiClient from "../services/apiClient";

// Create base store configuration for deadlines
const deadlinesBaseStore = createBaseStoreRefactor({
  endpoint: "/api/v1/deadlines",
  name: "DeadlinesStoreRefactor",
  objectName: "Deadline",
  enableCache: true,
  actionCableChannel: "DeadlinesChannel", // Class name
  infiniteLoading: false,
});

export const useDeadlinesStoreRefactor = defineStore("deadlinesRefactor", {
  ...deadlinesBaseStore,

  getters: {
    ...deadlinesBaseStore.getters,

    /** Get all deadlines from state */
    deadlines: (state) => {
      return state.data || [];
    },

    /** Return only open deadlines (not completed) */
    openDeadlines: (state) => {
      return (state.data || []).filter((d) => !d.completed_at);
    },

    /** Get deadline by ID */
    getDeadlineById: (state) => (id) => {
      return state.data.find((deadline) => deadline.id == id);
    },

    /** All open deadlines for a specific account, sorted by due date ASC */
    getDeadlinesByAccount: (state) => (accountId) => {
      const data = state.data || [];
      return data
        .filter(
          (d) => String(d.account_id) === String(accountId) && !d.completed_at,
        )
        .sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
    },

    /** Count of overdue (past due) open deadlines for an account */
    getPastDeadlinesCountByAccount: (state) => (accountId) => {
      const today = new Date();
      const data = state.data || [];
      return data.filter(
        (d) =>
          String(d.account_id) === String(accountId) &&
          !d.completed_at &&
          d.deadline &&
          new Date(d.deadline) < today,
      ).length;
    },

    /** Open deadlines assigned to a specific user, sorted by due date ASC */
    getOpenByUser: (state) => (userId) => {
      const data = state.data || [];
      return data
        .filter((d) => !d.completed_at && String(d.user_id) === String(userId))
        .sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
    },

    /** Open deadlines not assigned to a specific user (including everyone/null), sorted by due date ASC */
    getOpenNotByUser: (state) => (userId) => {
      const data = state.data || [];
      return data
        .filter((d) => !d.completed_at && String(d.user_id) !== String(userId))
        .sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
    },
  },

  actions: {
    ...deadlinesBaseStore.actions,

    async fetchOpen() {
      return await this.fetchAll({ params: { status: "open" } });
    },

    async fetchClosed() {
      return await this.fetchAll({ params: { status: "closed" } });
    },

    async fetchAllWithClosed() {
      return await this.fetchAll({ params: { status: "all" } });
    },

    async fetchOpenAndRecentClosed(days = 30) {
      try {
        this.loading = true;
        this.error = null;
        const result = await apiClient.get("/api/v1/deadlines", {
          params: { status: "open_and_recent", days },
        });
        if (Array.isArray(result)) {
          this.data = result;
          this.lastFetched = new Date();
        }
        return result;
      } catch (error) {
        this.error =
          error.message ||
          "Er is een fout opgetreden bij het laden van deadlines";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async close(deadlineOrId) {
      const id =
        typeof deadlineOrId === "object" ? deadlineOrId.id : deadlineOrId;
      const updated = await this.customApiCall("PATCH", `${id}/close`);
      // Upsert the updated record so it moves from open → recent closed dynamically
      if (updated && updated.id) {
        const idx = (this.data || []).findIndex((d) => d.id === updated.id);
        if (idx >= 0) {
          this.data.splice(idx, 1, updated);
        } else {
          this.data = [...(this.data || []), updated];
        }
      }
      return updated;
    },
  },
});
